import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star, CheckCircle2 } from "lucide-react";
import FileUploader from "@/components/FileUploader";

const TABS = [
  { key: "analytics", label: "Analytics" },
  { key: "products", label: "Products" },
  { key: "bookings", label: "Bookings" },
  { key: "content", label: "Content" },
  { key: "experiences", label: "Experiences" },
  { key: "orders", label: "Orders" },
  { key: "audience", label: "Audience" },
  { key: "circle", label: "Circle" },
];

const emptyProduct = { slug: "", title: "", description: "", long_description: "", price: 0, price_inr: null, type: "ebook", image: "", featured: false, exclusive: false, digital_file_url: "", spots_total: null, spots_remaining: null, series_slug: "", volume_number: null };
const emptyContent = { slug: "", title: "", type: "article", category: "Manifestation", excerpt: "", body: "", image: "", media_url: "", duration: "" };
const emptyTestimonial = { name: "", location: "", role: "", quote: "", avatar: "", rating: 5, featured: true, approved: true };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");
  const [products, setProducts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [remind, setRemind] = useState({ slug: "", link: "" });
  const [remindEmails, setRemindEmails] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);
  const [content, setContent] = useState([]);
  const [orders, setOrders] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [audience, setAudience] = useState({ total: 0, tag_counts: {}, subscribers: [] });
  const [audienceSearch, setAudienceSearch] = useState("");
  const [editing, setEditing] = useState(null); // {kind, data}
  const [analytics, setAnalytics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [circle, setCircle] = useState({ members: [], waitlist: [] });

  useEffect(() => {
    if (tab === "circle" && user?.role === "admin") {
      api.get("/admin/circle").then((r) => setCircle(r.data)).catch(() => {});
    }
  }, [tab, user]);

  useEffect(() => {
    if (tab === "analytics" && user?.role === "admin") {
      api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
      api.get("/admin/activity").then((r) => setActivity(r.data.items || [])).catch(() => {});
    }
  }, [tab, user]);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/", { replace: true });
      else if (user.role !== "admin") navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const refresh = async () => {
    try {
      const [p, b, c, o, t, a] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/bookings"),
        api.get("/admin/content"),
        api.get("/admin/orders"),
        api.get("/admin/testimonials"),
        api.get("/admin/newsletter"),
      ]);
      setProducts(p.data); setBookings(b.data); setContent(c.data); setOrders(o.data); setTestimonials(t.data); setAudience(a.data);
    } catch {
      toast.error("Could not load admin data");
    }
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (loading || !user || user.role !== "admin") {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-ink/50 font-serif italic" data-testid="admin-loading">arriving…</div>;
  }

  // ---- Product handlers ----
  const saveProduct = async (data) => {
    try {
      if (data.id) {
        const r = await api.put(`/admin/products/${data.id}`, data);
        if (r.data.delivered_count > 0) {
          toast.success(`Saved — download links emailed to ${r.data.delivered_count} pre-order buyer${r.data.delivered_count > 1 ? "s" : ""} 🌙`, { duration: 8000 });
        } else {
          toast.success("Saved");
        }
      } else {
        await api.post("/admin/products", data);
        toast.success("Saved");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Content handlers ----
  const saveContent = async (data) => {
    try {
      if (data.id) await api.put(`/admin/content/${data.id}`, data);
      else await api.post("/admin/content", data);
      toast.success("Saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteContent = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try { await api.delete(`/admin/content/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Testimonial handlers ----
  const saveTestimonial = async (data) => {
    try {
      if (data.id) await api.put(`/admin/testimonials/${data.id}`, data);
      else await api.post("/admin/testimonials", data);
      toast.success("Saved");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete this experience?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };
  const approveTestimonial = async (id) => {
    try { await api.patch(`/admin/testimonials/${id}/approve`); toast.success("Approved — now live on the homepage"); refresh(); }
    catch { toast.error("Approve failed"); }
  };

  // ---- Booking handlers ----
  const updateBookingStatus = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success(`Marked ${status}`); refresh(); }
    catch { toast.error("Update failed"); }
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 relative" data-testid="admin-page">
      <div className="overline mb-5">Admin</div>
      <h1 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight mb-10">
        The tending <em className="text-peach-deep">room</em>.
      </h1>

      <div className="flex flex-wrap gap-3 mb-10 border-b border-lapis/10 pb-3" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-sm transition font-medium ${
              tab === t.key ? "bg-lapis text-canvas" : "text-ink/65 hover:text-lapis"
            }`}>
            {t.label} <span className="opacity-60 text-xs ml-1">
              {t.key === "products" ? products.length : t.key === "bookings" ? bookings.length : t.key === "content" ? content.length : t.key === "experiences" ? testimonials.length : t.key === "audience" ? audience.total : orders.length}
            </span>
          </button>
        ))}
      </div>

      {tab === "analytics" && (
        <section data-testid="admin-analytics-section">
          {!analytics ? (
            <p className="text-ink/50 text-sm">Gathering the numbers…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {[
                  ["Souls today", analytics.unique_today, "unique visitors"],
                  ["Views today", analytics.views_today, "page views"],
                  ["Souls all-time", analytics.unique_visitors, "unique visitors"],
                  ["Views all-time", analytics.total_views, "page views"],
                ].map(([label, val, sub]) => (
                  <div key={label} className="bg-canvas border border-lapis/10 rounded-2xl p-6" data-testid={`analytics-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
                    <div className="overline mb-2">{label}</div>
                    <div className="font-serif text-4xl text-lapis">{val}</div>
                    <div className="text-xs text-ink/45 mt-1">{sub}</div>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-serif text-xl text-ink mb-4">Last 7 days</h3>
                  <div className="space-y-2">
                    {analytics.daily.length === 0 && <p className="text-sm text-ink/50">No visits recorded yet — share your link! 🌙</p>}
                    {analytics.daily.map((d) => (
                      <div key={d.day} className="flex items-center gap-3 text-sm">
                        <span className="w-24 text-ink/60">{d.day.slice(5)}</span>
                        <div className="flex-1 bg-lapis/5 rounded-full h-5 relative overflow-hidden">
                          <div className="bg-peach h-full rounded-full" style={{ width: `${Math.min(100, (d.views / Math.max(...analytics.daily.map((x) => x.views), 1)) * 100)}%` }} />
                        </div>
                        <span className="w-28 text-ink/70">{d.views} views · {d.visitors} souls</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-xl text-ink mb-4">Most visited pages</h3>
                  <div className="space-y-2">
                    {analytics.top_pages.map((p) => (
                      <div key={p.path} className="flex justify-between text-sm border-b border-lapis/10 pb-2">
                        <span className="text-ink/75 font-mono text-xs">{p.path}</span>
                        <span className="text-lapis font-semibold">{p.views}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-12" data-testid="admin-activity-feed">
                <h3 className="font-serif text-xl text-ink mb-4">Recent activity</h3>
                {activity.length === 0 ? (
                  <p className="text-sm text-ink/50">Quiet for now — your first order, booking or signup will appear here. 🌙</p>
                ) : (
                  <div className="space-y-3">
                    {activity.map((a, i) => (
                      <div key={i} className="flex items-center gap-4 bg-canvas border border-lapis/10 rounded-xl px-5 py-3" data-testid={`activity-item-${i}`}>
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${a.kind === "order" ? "bg-peach" : a.kind === "booking" ? "bg-lapis" : a.kind === "signup" ? "bg-sage" : "bg-lavender"}`} />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-ink">{a.title}</span>
                          <span className="text-sm text-ink/55 ml-3 truncate">{a.detail}</span>
                        </div>
                        <span className="text-xs text-ink/40 shrink-0">{(a.at || "").slice(0, 16).replace("T", " · ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {tab === "products" && (
        <section data-testid="admin-products-section">
          <div className="flex justify-end mb-5">
            <button onClick={() => setEditing({ kind: "product", data: { ...emptyProduct } })} className="btn-primary" data-testid="admin-add-product-btn">
              <Plus size={14} /> New product
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => (
              <div key={p.id} className="glass p-4 rounded-2xl" data-testid={`admin-product-${p.slug}`}>
                <img src={p.image} className="w-full h-40 object-cover rounded-xl mb-3" alt="" />
                <div className="overline mb-1">{p.type}{p.featured && " · featured"}</div>
                <div className="font-serif text-lg text-ink leading-tight">{p.title}</div>
                <div className="text-lapis font-medium mt-1">${p.price.toFixed(2)}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing({ kind: "product", data: { ...p } })} className="text-xs uppercase tracking-widest text-lapis hover:text-lapis-deep flex items-center gap-1" data-testid={`admin-edit-product-${p.slug}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => deleteProduct(p.id)} className="text-xs uppercase tracking-widest text-peach-deep hover:opacity-80 flex items-center gap-1" data-testid={`admin-delete-product-${p.slug}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "content" && (
        <section data-testid="admin-content-section">
          <div className="flex justify-end mb-5">
            <button onClick={() => setEditing({ kind: "content", data: { ...emptyContent } })} className="btn-primary" data-testid="admin-add-content-btn">
              <Plus size={14} /> New content
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {content.map((c) => (
              <div key={c.id} className="glass p-4 rounded-2xl" data-testid={`admin-content-${c.slug}`}>
                <img src={c.image} className="w-full h-32 object-cover rounded-xl mb-3" alt="" />
                <div className="overline mb-1">{c.type} · {c.category}</div>
                <div className="font-serif text-lg text-ink leading-tight">{c.title}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing({ kind: "content", data: { ...c } })} className="text-xs uppercase tracking-widest text-lapis hover:text-lapis-deep flex items-center gap-1" data-testid={`admin-edit-content-${c.slug}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => deleteContent(c.id)} className="text-xs uppercase tracking-widest text-peach-deep hover:opacity-80 flex items-center gap-1" data-testid={`admin-delete-content-${c.slug}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "bookings" && (
        <section data-testid="admin-bookings-section">
          <div className="glass p-5 rounded-2xl mb-6" data-testid="admin-manual-booking-card">
            <div className="font-serif text-lg text-ink mb-1">Add a client manually 💛</div>
            <p className="text-xs text-ink/55 mb-3">For bookings made through DMs / direct payment — adds them as a paid attendee so you can send them session links below.</p>
            <form
              className="grid grid-cols-1 md:grid-cols-5 gap-3"
              onSubmit={async (e) => {
                e.preventDefault();
                const f = e.target;
                try {
                  await api.post("/admin/bookings/manual", {
                    service_slug: f.msvc.value, name: f.mname.value, email: f.memail.value,
                    date: f.mdate.value, time_slot: f.mtime.value || "20:00",
                  });
                  toast.success(`${f.mname.value} added 💛`);
                  f.reset();
                  refresh();
                } catch { toast.error("Could not add client"); }
              }}
            >
              <select name="msvc" required className="border rounded-xl px-3 py-2 text-sm bg-white/80" data-testid="manual-booking-service">
                <option value="">Service…</option>
                {services.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
              <input name="mname" required placeholder="Client name" className="border rounded-xl px-3 py-2 text-sm bg-white/80" data-testid="manual-booking-name" />
              <input name="memail" required type="email" placeholder="Client email" className="border rounded-xl px-3 py-2 text-sm bg-white/80" data-testid="manual-booking-email" />
              <input name="mdate" required type="date" className="border rounded-xl px-3 py-2 text-sm bg-white/80" data-testid="manual-booking-date" />
              <div className="flex gap-2">
                <input name="mtime" placeholder="20:00" className="border rounded-xl px-3 py-2 text-sm bg-white/80 w-24" data-testid="manual-booking-time" />
                <button type="submit" className="btn-primary flex-1" data-testid="manual-booking-add-btn">Add</button>
              </div>
            </form>
          </div>
          <div className="glass p-5 rounded-2xl mb-6" data-testid="admin-remind-card">
            <div className="font-serif text-lg text-ink mb-1">Send session link / replay 🌙</div>
            <p className="text-xs text-ink/55 mb-3">Choose a service, tick the people you want, paste the link, send. Only ticked attendees receive it.
              <button
                type="button" data-testid="reconcile-bookings-btn"
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/bookings/reconcile");
                    if (r.data.fixed === 0) toast.info("All paid bookings are already in order 💛");
                    else toast.success(`Fixed ${r.data.fixed} booking(s): ${r.data.bookings.map((b) => b.name).join(", ")} 💛`);
                    refresh();
                  } catch { toast.error("Could not run the check"); }
                }}
                className="ml-2 text-lapis underline underline-offset-2 font-medium"
              >Missing someone who paid? Fix pending payments</button>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select value={remind.slug} onChange={(e) => { setRemind({ ...remind, slug: e.target.value }); setRemindEmails([]); }} className="border rounded-xl px-3 py-2 text-sm bg-white/80" data-testid="remind-service-select">
                <option value="">Choose service…</option>
                {services.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
              <input value={remind.link} onChange={(e) => setRemind({ ...remind, link: e.target.value })} placeholder="Meeting / replay link" className="border rounded-xl px-3 py-2 text-sm bg-white/80" data-testid="remind-link-input" />
              <div className="flex gap-2">
              <button
                type="button" data-testid="remind-send-btn"
                disabled={!remind.slug || !remind.link || remindEmails.length === 0}
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/bookings/remind", { service_slug: remind.slug, meeting_link: remind.link, emails: remindEmails });
                    toast.success(`Session link sent to ${r.data.sent} attendee${r.data.sent === 1 ? "" : "s"} ✨`);
                  } catch { toast.error("Could not send reminders"); }
                }}
                className="btn-primary disabled:opacity-40"
              >Send link</button>
              <button
                type="button" data-testid="replay-send-btn"
                disabled={!remind.slug || !remind.link || remindEmails.length === 0}
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/bookings/remind", { service_slug: remind.slug, meeting_link: remind.link, kind: "replay", emails: remindEmails });
                    toast.success(`Replay sent to ${r.data.sent} attendee${r.data.sent === 1 ? "" : "s"} 🌙`);
                  } catch { toast.error("Could not send replay"); }
                }}
                className="btn-primary disabled:opacity-40"
              >Send replay</button>
              </div>
            </div>
            {remind.slug && (() => {
              const seen = new Set();
              const attendees = bookings.filter((b) => b.service_slug === remind.slug && b.payment_status === "paid" && b.email && !seen.has(b.email.toLowerCase()) && seen.add(b.email.toLowerCase()));
              if (attendees.length === 0) return <p className="text-xs text-ink/50 mt-3 italic">No paid attendees for this service yet.</p>;
              const allOn = remindEmails.length === attendees.length;
              return (
                <div className="mt-4" data-testid="remind-attendee-picker">
                  <button
                    type="button" data-testid="remind-select-all-btn"
                    onClick={() => setRemindEmails(allOn ? [] : attendees.map((a) => a.email.toLowerCase()))}
                    className="text-xs font-medium text-lapis underline underline-offset-2 mb-2"
                  >{allOn ? "Unselect all" : `Select all (${attendees.length})`}</button>
                  <div className="flex flex-wrap gap-2">
                    {attendees.map((a) => {
                      const em = a.email.toLowerCase();
                      const on = remindEmails.includes(em);
                      return (
                        <button
                          key={em} type="button" data-testid={`remind-attendee-${em}`}
                          onClick={() => setRemindEmails(on ? remindEmails.filter((x) => x !== em) : [...remindEmails, em])}
                          className={`px-3 py-1.5 rounded-full text-xs border-2 transition font-medium ${on ? "bg-lapis text-canvas border-lapis" : "border-lapis/25 text-ink/70 bg-white/60 hover:border-lapis"}`}
                        >{on ? "✓ " : ""}{a.name || em}</button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-ink/45 mt-2">{remindEmails.length} selected — only they will receive the email.</p>
                </div>
              );
            })()}
          </div>
          {bookings.length === 0 ? (
            <p className="text-ink/50 font-serif italic">No bookings yet.</p>
          ) : (
            <div className="space-y-4">
              {bookings.map((b) => (
                <div key={b.id} className="glass p-5 rounded-2xl grid grid-cols-1 md:grid-cols-5 gap-4 items-start" data-testid={`admin-booking-${b.id}`}>
                  <div>
                    <div className="font-serif text-lg text-ink">{b.service_title}</div>
                    <div className="text-xs text-ink/60 mt-1">{b.date} · {b.time_slot}</div>
                  </div>
                  <div>
                    <div className="text-sm text-ink">{b.name}</div>
                    <div className="text-xs text-ink/60">{b.email}</div>
                    {b.phone && <div className="text-xs text-ink/60">{b.phone}</div>}
                  </div>
                  <div className="md:col-span-2 text-sm text-ink/70 italic">{b.intention || "—"}</div>
                  <div className="flex flex-col gap-2">
                    <select
                      value={b.status}
                      onChange={(e) => updateBookingStatus(b.id, e.target.value)}
                      className="px-3 py-1 rounded-full text-xs uppercase tracking-widest border border-lapis/30 bg-white"
                      data-testid={`admin-booking-status-${b.id}`}
                    >
                      <option value="pending">pending</option>
                      <option value="confirmed">confirmed</option>
                      <option value="completed">completed</option>
                      <option value="cancelled">cancelled</option>
                    </select>
                    <button onClick={() => deleteBooking(b.id)} className="text-xs uppercase tracking-widest text-peach-deep hover:opacity-80 flex items-center gap-1 justify-center" data-testid={`admin-delete-booking-${b.id}`}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "orders" && (
        <section data-testid="admin-orders-section">
          <div className="flex justify-end gap-3 mb-5 flex-wrap">
            <button
              onClick={async () => {
                try {
                  const r = await api.post("/admin/decode/gift-emails?dry_run=true");
                  const n = r.data.to_send?.length || 0;
                  if (n === 0) { toast.info("Every Decode buyer has already received the gift email 💛"); return; }
                  if (!window.confirm(`Send the upgraded Decode gift email to ${n} buyer${n === 1 ? "" : "s"}?\n\n${r.data.to_send.join("\n")}`)) return;
                  const s = await api.post("/admin/decode/gift-emails?dry_run=false");
                  toast.success(`Gift email sent to ${s.data.sent} buyer${s.data.sent === 1 ? "" : "s"} 💛`);
                } catch (e) { toast.error("Could not send gift emails"); }
              }}
              className="btn-primary"
              data-testid="admin-decode-gift-btn"
            >
              Send Decode upgrade gift 💛
            </button>
            <button
              onClick={async () => {
                try {
                  const r = await api.post("/admin/journal/beginning-upgrade-emails?dry_run=true");
                  const n = r.data.to_send?.length || 0;
                  if (n === 0) { toast.info("Every Beginning buyer has already received the upgrade email 🌙"); return; }
                  if (!window.confirm(`Send the new art-cover edition + series news to ${n} buyer${n === 1 ? "" : "s"}?\n\n${r.data.to_send.join("\n")}`)) return;
                  const s = await api.post("/admin/journal/beginning-upgrade-emails?dry_run=false");
                  toast.success(`Upgrade email sent to ${s.data.sent} buyer${s.data.sent === 1 ? "" : "s"} 🌙`);
                } catch (e) { toast.error("Could not send upgrade emails"); }
              }}
              className="btn-primary"
              data-testid="admin-beginning-upgrade-btn"
            >
              Send Beginning upgrade 🌙
            </button>
          </div>
          {orders.length === 0 ? (
            <p className="text-ink/50 font-serif italic">No orders yet.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="glass p-4 rounded-2xl flex justify-between items-center" data-testid={`admin-order-${o.id}`}>
                  <div>
                    <div className="font-mono text-xs text-ink/50">{o.id?.slice(0, 8)}</div>
                    <div className="text-sm text-ink">{o.user_email || "guest"}</div>
                    <div className="text-xs text-ink/50">{o.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="font-serif text-xl text-lapis">${o.amount?.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "experiences" && (
        <section data-testid="admin-experiences-section">
          <div className="flex justify-between items-center mb-5 gap-4">
            <div className="text-sm text-ink/65">
              {testimonials.filter(t => !t.approved).length > 0 && (
                <span className="text-peach-deep font-medium" data-testid="admin-pending-count">
                  {testimonials.filter(t => !t.approved).length} pending review
                </span>
              )}
            </div>
            <button onClick={() => setEditing({ kind: "testimonial", data: { ...emptyTestimonial } })} className="btn-primary" data-testid="admin-add-testimonial-btn">
              <Plus size={14} /> New experience
            </button>
          </div>
          {testimonials.length === 0 ? (
            <p className="text-ink/50 font-serif italic">No experiences yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className={`glass p-5 rounded-2xl ${!t.approved ? "ring-2 ring-peach-deep/40" : ""}`} data-testid={`admin-testimonial-${t.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="overline">{t.approved ? (t.featured ? "live · featured" : "approved · hidden") : "pending"}</div>
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={11} strokeWidth={1.4}
                          style={i <= (t.rating || 5) ? { fill: "#d4a574", color: "#d4a574" } : { color: "rgba(26,37,48,0.2)" }} />
                      ))}
                    </div>
                  </div>
                  <p className="font-serif italic text-ink/85 leading-snug line-clamp-4">"{t.quote}"</p>
                  <div className="mt-3 text-sm text-ink">{t.name}</div>
                  <div className="text-xs text-ink/55">{[t.role, t.location].filter(Boolean).join(" · ")}</div>
                  <div className="mt-3 flex gap-3 flex-wrap">
                    {!t.approved && (
                      <button onClick={() => approveTestimonial(t.id)} className="text-xs uppercase tracking-widest text-sage-deep hover:opacity-80 flex items-center gap-1 font-semibold" data-testid={`admin-approve-testimonial-${t.id}`}>
                        <CheckCircle2 size={12} /> Approve
                      </button>
                    )}
                    <button onClick={() => setEditing({ kind: "testimonial", data: { ...t } })} className="text-xs uppercase tracking-widest text-lapis hover:text-lapis-deep flex items-center gap-1" data-testid={`admin-edit-testimonial-${t.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => deleteTestimonial(t.id)} className="text-xs uppercase tracking-widest text-peach-deep hover:opacity-80 flex items-center gap-1" data-testid={`admin-delete-testimonial-${t.id}`}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {editing && (
        <EditModal
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={editing.kind === "product" ? saveProduct : editing.kind === "testimonial" ? saveTestimonial : saveContent}
        />
      )}

      {tab === "circle" && (
        <section data-testid="admin-circle-section">
          <h2 className="font-serif text-2xl text-ink mb-4">Inner Circle members</h2>
          <div className="overflow-x-auto rounded-2xl border border-lapis/10 bg-white/70 mb-8">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink/55 border-b border-lapis/10">
                <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">WhatsApp</th><th className="p-3">Plan</th><th className="p-3">₹</th><th className="p-3">Payment</th><th className="p-3">Payment ID</th><th className="p-3">Date</th>
              </tr></thead>
              <tbody>
                {circle.members.map((m) => (
                  <tr key={m.id} className="border-b border-lapis/5" data-testid={`circle-member-${m.id}`}>
                    <td className="p-3 font-medium">{m.name}</td><td className="p-3">{m.email}</td><td className="p-3">{m.whatsapp}</td>
                    <td className="p-3">{m.plan}</td><td className="p-3">{m.amount}</td>
                    <td className="p-3"><span className={m.payment_status === "paid" ? "text-sage font-semibold" : "text-peach-deep"}>{m.payment_status}</span></td>
                    <td className="p-3 text-xs">{m.payment_id || "—"}</td>
                    <td className="p-3 text-xs">{(m.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
                {circle.members.length === 0 && <tr><td className="p-4 text-ink/50" colSpan={8}>No members yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <h2 className="font-serif text-2xl text-ink mb-4">Waitlist</h2>
          <div className="overflow-x-auto rounded-2xl border border-lapis/10 bg-white/70">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink/55 border-b border-lapis/10"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">WhatsApp</th><th className="p-3">Date</th></tr></thead>
              <tbody>
                {circle.waitlist.map((w) => (
                  <tr key={w.id} className="border-b border-lapis/5"><td className="p-3">{w.name}</td><td className="p-3">{w.email}</td><td className="p-3">{w.whatsapp}</td><td className="p-3 text-xs">{(w.created_at || "").slice(0, 10)}</td></tr>
                ))}
                {circle.waitlist.length === 0 && <tr><td className="p-4 text-ink/50" colSpan={4}>No one waiting.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "audience" && (
        <section data-testid="admin-audience-section">
          <div className="flex flex-wrap gap-3 mb-6">
            {Object.entries(audience.tag_counts).map(([k, v]) => (
              <div key={k} className="glass px-4 py-2 rounded-full text-sm" data-testid={`audience-tag-${k}`}>
                <span className="text-ink/55 capitalize">{k.replace(/_/g, " ")}</span>
                <span className="ml-2 font-serif text-lapis text-base">{v}</span>
              </div>
            ))}
            <div className="ml-auto">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/newsletter/export`}
                className="btn-soft"
                data-testid="audience-export-btn"
              >
                Download CSV
              </a>
            </div>
          </div>
          <input
            type="search"
            placeholder="Search by email or name…"
            className="eth-input mb-6"
            value={audienceSearch}
            onChange={(e) => setAudienceSearch(e.target.value)}
            data-testid="audience-search"
          />
          {audience.subscribers.length === 0 ? (
            <p className="text-ink/50 font-serif italic">No subscribers yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audience-table">
                <thead>
                  <tr className="text-left text-ink/55 border-b border-lapis/10">
                    <th className="py-3 pr-4 overline text-xs">Email</th>
                    <th className="py-3 pr-4 overline text-xs">Name</th>
                    <th className="py-3 pr-4 overline text-xs">Tags</th>
                    <th className="py-3 pr-4 overline text-xs">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {audience.subscribers
                    .filter((s) => {
                      const q = audienceSearch.toLowerCase();
                      if (!q) return true;
                      return (s.email || "").toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q);
                    })
                    .map((s, i) => (
                      <tr key={s.email + i} className="border-b border-lapis/5 hover:bg-cream/30" data-testid={`audience-row-${i}`}>
                        <td className="py-3 pr-4 text-ink">{s.email}</td>
                        <td className="py-3 pr-4 text-ink/70">{s.name || "—"}</td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-wrap gap-1.5">
                            {(s.tags || []).length === 0 ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-cream text-ink/55">general</span>
                            ) : (
                              (s.tags || []).map((tag) => (
                                <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-peach/30 text-peach-deep capitalize">{tag.replace(/_/g, " ")}</span>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-ink/55 text-xs whitespace-nowrap">{(s.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function EditModal({ editing, onClose, onSave }) {
  const [form, setForm] = useState(editing.data);
  const isProduct = editing.kind === "product";
  const isTestimonial = editing.kind === "testimonial";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="admin-edit-modal">
      <div className="glass-strong rounded-[1.75rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 text-ink/60 hover:text-lapis" data-testid="admin-modal-close">
          <X size={20} />
        </button>
        <div className="overline mb-3">{form.id ? "Edit" : "New"} {isProduct ? "product" : isTestimonial ? "experience" : "content"}</div>
        <h2 className="font-serif text-3xl text-lapis mb-8 font-light">
          {isTestimonial ? (form.name || "Untitled") : (form.title || (isProduct ? "Untitled product" : "Untitled content"))}
        </h2>

        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          {isTestimonial ? (
            <>
              <div>
                <label className="overline block mb-2">Name</label>
                <input required className="eth-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="admin-form-name" />
              </div>
              <div>
                <label className="overline block mb-2">Location</label>
                <input className="eth-input" value={form.location || ""} onChange={(e) => set("location", e.target.value)} data-testid="admin-form-location" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Role / Context</label>
                <input className="eth-input" placeholder="1:1 mentee, Workshop attendee…" value={form.role || ""} onChange={(e) => set("role", e.target.value)} data-testid="admin-form-role" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Quote</label>
                <textarea required rows={5} className="eth-input resize-none" value={form.quote} onChange={(e) => set("quote", e.target.value)} data-testid="admin-form-quote" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Avatar URL (optional)</label>
                <input className="eth-input" value={form.avatar || ""} onChange={(e) => set("avatar", e.target.value)} data-testid="admin-form-avatar" />
                <div className="mt-2">
                  <FileUploader accept="image/*" label="Upload avatar" current={form.avatar} onUpload={(d) => set("avatar", d.url)} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="exp-featured" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="admin-form-testimonial-featured" />
                <label htmlFor="exp-featured" className="overline cursor-pointer">Show on homepage</label>
              </div>
            </>
          ) : (
            <>
          <div>
            <label className="overline block mb-2">Slug</label>
            <input required className="eth-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} data-testid="admin-form-slug" />
          </div>
          <div>
            <label className="overline block mb-2">Title</label>
            <input required className="eth-input" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-form-title" />
          </div>

          {isProduct ? (
            <>
              <div>
                <label className="overline block mb-2">Price (USD)</label>
                <input required type="number" step="0.01" className="eth-input" value={form.price} onChange={(e) => set("price", parseFloat(e.target.value))} data-testid="admin-form-price" />
              </div>
              <div>
                <label className="overline block mb-2">Price (INR — optional)</label>
                <input type="number" step="1" className="eth-input" placeholder="leave blank to auto-convert" value={form.price_inr ?? ""} onChange={(e) => set("price_inr", e.target.value === "" ? null : parseFloat(e.target.value))} data-testid="admin-form-price-inr" />
              </div>
              <div>
                <label className="overline block mb-2">Type</label>
                <select className="eth-input" value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="admin-form-type">
                  <option value="ebook">ebook</option>
                  <option value="audio">audio</option>
                  <option value="physical">physical</option>
                  <option value="bundle">bundle</option>
                  <option value="signature">signature</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="exclusive" checked={!!form.exclusive} onChange={(e) => set("exclusive", e.target.checked)} data-testid="admin-form-exclusive" />
                <label htmlFor="exclusive" className="overline cursor-pointer">Only here (exclusive)</label>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Image URL</label>
                <input required className="eth-input" value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="admin-form-image" />
                <div className="mt-2">
                  <FileUploader
                    accept="image/*"
                    label="Upload image"
                    current={form.image}
                    onUpload={(d) => set("image", d.url)}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Short description</label>
                <input required className="eth-input" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="admin-form-description" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Long description</label>
                <textarea rows={4} className="eth-input resize-none" value={form.long_description || ""} onChange={(e) => set("long_description", e.target.value)} data-testid="admin-form-long-description" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Digital file (PDF / MP3 — only for ebook / audio / bundle)</label>
                <input className="eth-input" value={form.digital_file_url || ""} placeholder="/api/files/{id} or external URL" onChange={(e) => set("digital_file_url", e.target.value)} data-testid="admin-form-digital-url" />
                <div className="mt-2">
                  <FileUploader
                    accept=".pdf,.mp3,.wav,.m4a"
                    label="Upload PDF / audio"
                    current={form.digital_file_url}
                    onUpload={(d) => set("digital_file_url", d.url)}
                  />
                </div>
                <p className="mt-2 text-xs text-ink/50">Emailed to the customer automatically when their order is paid.</p>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="featured" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="admin-form-featured" />
                <label htmlFor="featured" className="overline cursor-pointer">Featured</label>
              </div>

              <div className="md:col-span-2 border-t border-lapis/10 pt-6 mt-2">
                <div className="overline mb-3 text-peach-deep">Series (optional)</div>
              </div>
              <div>
                <label className="overline block mb-2">Series slug</label>
                <input className="eth-input" placeholder="e.g. 21-days-magnet-of-miracles" value={form.series_slug || ""} onChange={(e) => set("series_slug", e.target.value)} data-testid="admin-form-series-slug" />
              </div>
              <div>
                <label className="overline block mb-2">Volume number</label>
                <input type="number" min="1" className="eth-input" value={form.volume_number ?? ""} onChange={(e) => set("volume_number", e.target.value === "" ? null : parseInt(e.target.value))} data-testid="admin-form-volume-number" />
              </div>

              <div className="md:col-span-2 border-t border-lapis/10 pt-6 mt-2">
                <div className="overline mb-3 text-peach-deep">Limited spots (for signature offerings)</div>
              </div>
              <div>
                <label className="overline block mb-2">Spots total</label>
                <input type="number" min="0" className="eth-input" placeholder="e.g. 5" value={form.spots_total ?? ""} onChange={(e) => set("spots_total", e.target.value === "" ? null : parseInt(e.target.value))} data-testid="admin-form-spots-total" />
              </div>
              <div>
                <label className="overline block mb-2">Spots remaining</label>
                <input type="number" min="0" className="eth-input" placeholder="e.g. 3" value={form.spots_remaining ?? ""} onChange={(e) => set("spots_remaining", e.target.value === "" ? null : parseInt(e.target.value))} data-testid="admin-form-spots-remaining" />
                <p className="text-xs text-ink/45 mt-2">Both blank to hide the counter on the page.</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="overline block mb-2">Type</label>
                <select className="eth-input" value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="admin-form-type">
                  <option value="article">article</option>
                  <option value="audio">audio</option>
                  <option value="video">video</option>
                  <option value="affirmation">affirmation</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Category</label>
                <input required className="eth-input" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="admin-form-category" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Image URL</label>
                <input required className="eth-input" value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="admin-form-image" />
                <div className="mt-2">
                  <FileUploader accept="image/*" label="Upload image" current={form.image} onUpload={(d) => set("image", d.url)} />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Media URL (audio/video)</label>
                <input className="eth-input" value={form.media_url || ""} onChange={(e) => set("media_url", e.target.value)} data-testid="admin-form-media" />
                <div className="mt-2">
                  <FileUploader accept=".mp3,.wav,.m4a,.mp4" label="Upload media" current={form.media_url} onUpload={(d) => set("media_url", d.url)} />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Duration label</label>
                <input className="eth-input" value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} placeholder="6 min read" data-testid="admin-form-duration" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Excerpt</label>
                <input required className="eth-input" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="admin-form-excerpt" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Body</label>
                <textarea rows={5} className="eth-input resize-none" value={form.body || ""} onChange={(e) => set("body", e.target.value)} data-testid="admin-form-body" />
              </div>
            </>
          )}
            </>
          )}

          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-soft" data-testid="admin-form-cancel">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="admin-form-save">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
